import React from 'react';
import {
  Box, Typography, Grid, Divider, Chip, Alert, Paper, Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import SeverityLevelBadge from './SeverityLevelBadge';
import {
  translatePresentingIssue,
  translateGender,
  translateEmployeeRelationship,
  translateDeliveryMethod,
  translateRiskType,
  translateEmployeeType,
  translateReferralSource,
  translateAgeRange,
  translateLengthOfService
} from '../../utils/catalogTranslations';
import type { ReferralIntakeContext } from '../../types/referralIntakeContext';

interface IntakeContextPanelProps {
  context: ReferralIntakeContext | null;
  loading?: boolean;
}

const Field: React.FC<{ label: string; value?: string | number | null; fullWidth?: boolean }> = ({
  label,
  value,
  fullWidth = false
}) => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return null;
  }
  return (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {value}
      </Typography>
    </Grid>
  );
};

const boolLabel = (value?: boolean | null) => {
  if (value === true) return 'Sí';
  if (value === false) return 'No';
  return undefined;
};

const surveyStatusLabel: Record<string, string> = {
  DRAFT: 'En captura',
  SUBMITTED: 'Completada',
  DECLINED: 'Declinada'
};

const phoneCallStatusLabel: Record<string, string> = {
  DRAFT: 'Borrador',
  CAPTURED: 'Capturada',
  ASSIGNED: 'Asignada',
  IN_PROGRESS: 'En progreso',
  EVIDENCE_PENDING: 'Evidencia pendiente',
  COMPLETED: 'Completada'
};

const familyRelationshipLabel: Record<string, string> = {
  SPOUSE: 'Cónyuge',
  CHILD: 'Hijo(a)',
  OTHER: 'Otro'
};

const IntakeContextPanel: React.FC<IntakeContextPanelProps> = ({ context, loading }) => {
  if (loading) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Cargando contexto del intake telefónico...
        </Typography>
      </Box>
    );
  }

  if (!context) {
    return (
      <Typography variant="body2" color="text.secondary">
        No hay contexto de intake disponible para este caso.
      </Typography>
    );
  }

  const employee = context.employee;
  const patient = context.patient;
  const referral = context.referral;
  const employeeName = employee
    ? [employee.firstName, employee.middleName, employee.lastName].filter(Boolean).join(' ')
    : '';
  const patientPrivateName = patient
    ? [patient.privateFirstName, patient.privateMiddleName, patient.privateLastName].filter(Boolean).join(' ')
    : '';
  const patientPublicName = patient
    ? [patient.firstName, patient.lastName].filter(Boolean).join(' ')
    : '';

  const consultationReason = referral?.comments?.trim() || '';
  const catalogPresentingIssue =
    referral?.presentingIssue && referral.presentingIssue !== 'Sin motivo detallado'
      ? translatePresentingIssue(referral.presentingIssue)
      : '';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Contexto del intake telefónico
        </Typography>
        <SeverityLevelBadge severityLevel={context.severityLevel} />
        {context.hadCrisis && <Chip label="Crisis" color="error" size="small" />}
        {context.phoneCallStatus && (
          <Chip
            label={phoneCallStatusLabel[context.phoneCallStatus] || context.phoneCallStatus}
            size="small"
            variant="outlined"
          />
        )}
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'primary.50', borderColor: 'primary.200' }}>
        <Typography variant="subtitle2" color="primary.main" gutterBottom>
          Motivo de la consulta
        </Typography>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontWeight: consultationReason ? 500 : 400 }}>
          {consultationReason || 'No se capturó motivo de consulta en el intake.'}
        </Typography>
        {catalogPresentingIssue && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Catálogo ComPsych: {catalogPresentingIssue}
          </Typography>
        )}
      </Paper>

      {referral?.presentingIssue === 'Sin motivo detallado' && !consultationReason && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No hay motivo de consulta capturado. Revisa el intake antes de enviar a ComPsych.
        </Alert>
      )}

      {referral && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Caso / servicio capturado
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Field label="Producto" value={referral.product} />
            <Field label="Subcategoría" value={referral.subCategory} />
            <Field label="Método de entrega" value={translateDeliveryMethod(referral.deliveryMethod)} />
            <Field label="Tipo de riesgo" value={translateRiskType(referral.riskType)} />
            <Field label="Tipo de empleado" value={translateEmployeeType(referral.employeeType)} />
            <Field label="Origen (catálogo ComPsych)" value={translateReferralSource(referral.referralSource)} />
            <Field label="Incidente especial" value={referral.specialIncident} />
            <Field label="Rango de edad (caso)" value={translateAgeRange(referral.ageRange)} />
            <Field label="Location number" value={referral.locationNumber} />
            <Field
              label="Fecha del caso"
              value={referral.referralCallDate ? new Date(referral.referralCallDate + 'T12:00:00').toLocaleDateString('es-MX') : undefined}
            />
            <Field
              label="Coaching embarazo (HWB)"
              value={boolLabel(referral.pregnancyCoaching)}
            />
          </Grid>
        </>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" gutterBottom>
        Llamada
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Field label="ID llamada" value={context.phoneCallId} />
        <Field label="Estado de llamada" value={context.phoneCallStatus ? phoneCallStatusLabel[context.phoneCallStatus] || context.phoneCallStatus : undefined} />
        <Field label="Duración" value={context.duration} />
        <Field label="Fecha de llamada" value={context.callDate ? new Date(context.callDate).toLocaleString('es-MX') : undefined} />
        <Field label="¿Cómo se enteró?" value={context.referralSourceIntake} fullWidth />
        <Field label="Seguimiento asignado a" value={context.assignedToUsername} />
        <Field
          label="Fecha asignación seguimiento"
          value={context.assignedToDate ? new Date(context.assignedToDate).toLocaleString('es-MX') : undefined}
        />
        <Field label="Capturado por" value={context.createdByUsername} />
        <Field label="¿Hubo crisis?" value={boolLabel(context.hadCrisis)} />
        <Field label="Notas de crisis" value={context.crisisNotes} fullWidth />
        <Field label="Observaciones de llamada" value={context.observations} fullWidth />
        <Field label="Aviso de privacidad aceptado" value={boolLabel(context.privacyConsent)} />
        <Field
          label="Fecha aceptación privacidad"
          value={context.privacyConsentDate ? new Date(context.privacyConsentDate).toLocaleString('es-MX') : undefined}
        />
        <Field label="Versión aviso de privacidad" value={context.privacyVersion} />
        <Field label="IP aceptación privacidad" value={context.privacyConsentIp} />
      </Grid>

      {employee && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom>
            Empleado
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Field label="Nombre" value={employeeName} />
            <Field label="Edad" value={employee.age} />
            <Field label="Género" value={translateGender(employee.gender)} />
            <Field label="Email" value={employee.email} />
            <Field label="Tel. móvil" value={employee.mobilePhone} />
            <Field label="Tel. casa" value={employee.homePhone} />
            <Field label="Ciudad" value={employee.city} />
            <Field label="Clave estado (cveEnt)" value={employee.cveEnt} />
            <Field label="Clave municipio (cveMun)" value={employee.cveMun} />
            <Field label="Clave localidad (cveLoc)" value={employee.cveLoc} />
            <Field label="Puesto" value={employee.position} />
            <Field label="Estado civil" value={employee.maritalStatus} />
            <Field label="Hijos" value={employee.numberOfChildren} />
            <Field label="Empresa" value={employee.accountOrgPath} fullWidth />
          </Grid>
        </>
      )}

      {context.familyMembers && context.familyMembers.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
            Familiares del empleado
          </Typography>
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Relación</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Edad</TableCell>
                <TableCell>Género</TableCell>
                <TableCell>Contacto principal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {context.familyMembers.map((member, index) => (
                <TableRow key={`${member.name}-${index}`}>
                  <TableCell>{familyRelationshipLabel[member.relationshipType || ''] || member.relationshipType}</TableCell>
                  <TableCell>
                    {[member.name, member.paternalLastName, member.maternalLastName].filter(Boolean).join(' ')}
                  </TableCell>
                  <TableCell>{member.age ?? '—'}</TableCell>
                  <TableCell>{translateGender(member.gender)}</TableCell>
                  <TableCell>{member.primaryContact ? 'Sí' : 'No'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {patient && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom>
            Paciente / beneficiario
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Field label="Nombre (ComPsych)" value={patientPublicName} />
            <Field label="Nombre privado" value={patientPrivateName} />
            <Field label="Email" value={patient.email} />
            <Field label="Tel. móvil privado" value={patient.privateMobilePhone} />
            <Field label="Tel. casa privado" value={patient.privateHomePhone} />
            <Field label="Género" value={translateGender(patient.gender)} />
            <Field label="Rango de edad" value={translateAgeRange(patient.ageRange)} />
            <Field label="Tiempo de servicio" value={translateLengthOfService(patient.lengthOfService)} />
            <Field label="País" value={patient.countryAbbreviation} />
            <Field label="Relación con empleado" value={translateEmployeeRelationship(patient.employeeRelationship)} />
            <Field label="Descripción de relación" value={patient.employeeRelationshipDescription} fullWidth />
            <Field
              label="Tratamiento psicológico previo"
              value={boolLabel(patient.privatePreviousPsychologicalTreatment)}
            />
          </Grid>
        </>
      )}

      {referral && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom>
            Especialista asignado en intake
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Field label="Especialista" value={referral.assignedSpecialistName} />
            <Field
              label="Fecha de asignación"
              value={referral.assignmentDate ? new Date(referral.assignmentDate).toLocaleString('es-MX') : undefined}
            />
            <Field label="Estatus de asignación" value={referral.assignmentStatus} />
            <Field label="Notas de asignación" value={referral.assignmentNotes} fullWidth />
          </Grid>
        </>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" gutterBottom>
        Encuesta EAP
      </Typography>
      {context.surveys && context.surveys.length > 0 ? (
        context.surveys.map((survey, surveyIndex) => (
          <Paper key={survey.clientSurveyId || surveyIndex} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {survey.surveyTemplateName || 'Encuesta'}
              </Typography>
              {survey.status && (
                <Chip
                  size="small"
                  label={surveyStatusLabel[survey.status] || survey.status}
                  color={survey.status === 'SUBMITTED' ? 'success' : survey.status === 'DECLINED' ? 'default' : 'warning'}
                />
              )}
            </Box>
            {survey.submittedAt && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Enviada: {new Date(survey.submittedAt).toLocaleString('es-MX')}
              </Typography>
            )}
            {survey.answers && survey.answers.length > 0 ? (
              survey.answers.map((answer, index) => (
                <Box key={`${answer.questionText}-${index}`} sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {answer.questionText}
                  </Typography>
                  <Typography variant="body2">{answer.answerText || '—'}</Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                {survey.status === 'DECLINED'
                  ? 'El paciente declinó contestar la encuesta.'
                  : 'Sin respuestas registradas.'}
              </Typography>
            )}
          </Paper>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No aplica encuesta o no hay registros para este paciente en la llamada.
        </Typography>
      )}

      {context.otherServicesOnCall && context.otherServicesOnCall.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom>
            Otros servicios en la misma llamada
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Paciente</TableCell>
                <TableCell>Producto</TableCell>
                <TableCell>Entrega</TableCell>
                <TableCell>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {context.otherServicesOnCall.map((service) => (
                <TableRow key={service.referralId}>
                  <TableCell>{service.patientName}</TableCell>
                  <TableCell>{service.product}</TableCell>
                  <TableCell>{translateDeliveryMethod(service.deliveryMethod)}</TableCell>
                  <TableCell>{service.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
};

export default IntakeContextPanel;
